import React, { useContext, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import LeadButtons from "../../../../../components/organisms/LeadHistory/LeadButtons";
import { AppDispatch } from "../../../../../slices/store";
import {
  getSingleLead,
  patchLeadAction,
} from "../../../../../slices/leads/leadManagementSlice";
import { scrollToTop } from "../../../../../utils/helpers/helperMethods/scrollToTop";
import { protectedResources } from "../../../../../app/authConfig";
import { TodosContext } from "../../../../../context";
import { UserRole } from "../../../../../utils";

export enum LeadActionType {
  Back = "Back",
  Delete = "Delete",
  Location = "Location",
  SocialMedia = "Social",
  CompanyProfile = "Identity Intelligence",
  Chat = "Chat",
  Call = "Call",
  Like = "Like",
  Dislike = "Dislike",
  View = "View",
}

interface LeadActionsProps {
  selectedLead: any;
  setModals: (modalState: any) => void;
}

const LeadActions: React.FC<LeadActionsProps> = ({
  selectedLead,
  setModals,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { instance } = useMsal();
  const activeAccount = instance?.getActiveAccount();
  const userRole = activeAccount?.idTokenClaims?.extension_ilpr as string;
  const { addAlert } = useContext(TodosContext);
  const { id = "" } = useParams<{ id: string }>();

  const reqParams = {
    url: process.env.REACT_APP_API_TODO_LIST_ENDPOINT || "",
    accessToken: localStorage.getItem("AccessToken") || "",
    id,
  };

  // Trigger view tracking on mount if lead is not yet viewed
  useEffect(() => {
    if (
      userRole === UserRole.Customer &&
      !selectedLead?.assignedUsers?.[0]?.isViewed
    ) {
      handleLeadAction(LeadActionType.View);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.assignedUsers]);

  // Dispatch backend action (Like, Dislike, View, etc.)
  const handleLeadAction = async (action: LeadActionType) => {
    const params = { ...reqParams, action };

    if (!params.url) {
      console.error("API URL is not defined");
      return;
    }

    await dispatch(patchLeadAction(params)).then(async (r: any) => {
      scrollToTop();

      const result = await dispatch(getSingleLead(reqParams));
      if (result?.payload) {
        addAlert("Your Action Updated Successfully", "success", true);
      } else {
        addAlert("Failed to refresh lead data", "error", true);
      }
    });
  };

  const modalKeyMap: Partial<
    Record<LeadActionType, "location" | "social" | "confirm" | "chat" | "IdentityIntelligence">
  > = {
    [LeadActionType.Delete]: "confirm",
    [LeadActionType.Location]: "location",
    [LeadActionType.SocialMedia]: "social",
    [LeadActionType.Chat]: "chat",
    [LeadActionType.CompanyProfile]: "IdentityIntelligence",
  };

  // Handle all UI button interactions
  const handleClick = (action: LeadActionType) => {
    switch (action) {
      case LeadActionType.Back:
        window.history.back();
        return;

      case LeadActionType.Call:
        if (selectedLead?.mobile) {
          window.location.href = `tel:${selectedLead.mobile}`;
        }
        return;

      case LeadActionType.Like:
      case LeadActionType.Dislike:
        handleLeadAction(action);
        return;
    }

    const modalKey = modalKeyMap[action];
    if (modalKey) {
      setModals((prev: any) => ({
        ...prev,
        [modalKey]: true,
      }));
    }
  };

  return (
    <LeadButtons
      selectedLead={selectedLead}
      userRole={userRole}
      MainHandleFunction={(e: any, type: string) =>
        handleClick(type as LeadActionType)
      }
    />
  );
};

export default LeadActions;
